'use client';

import { Button } from '../../../components/button';
import { Box, Flex } from '@terra/ui-styled-system';
import type { ChatSuggestionCategoryPillsProps } from './types';

export function ChatSuggestionCategoryPills({
  categories,
  activeCategoryId,
  onSelectCategory,
  variant = 'icon-pill',
  pillRowIndexes,
}: ChatSuggestionCategoryPillsProps) {
  const renderPill = (categoryId: string) => {
    const category = categories.find((item) => item.id === categoryId);

    if (!category) {
      return null;
    }

    const Icon = category.Icon;
    const isActive = activeCategoryId === category.id;

    if (variant === 'text-pill') {
      return (
        <Button
          key={category.id}
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onSelectCategory(isActive ? null : category.id)}
          borderRadius="l2"
          px={3}
          py={1.5}
          h="auto"
          fontWeight="normal"
          fontSize="sm"
          flexShrink={0}
          bg="transparent"
          borderColor={isActive ? 'border.default' : 'border.muted'}
        >
          {category.pillLabel}
        </Button>
      );
    }

    return (
      <Button
        key={category.id}
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onSelectCategory(isActive ? null : category.id)}
        borderRadius="full"
        px={3}
        py={2}
        h="auto"
        fontWeight="medium"
        fontSize="xs"
        gap={2}
        flexShrink={0}
        bg={isActive ? 'bg.muted' : 'transparent'}
        borderColor={isActive ? 'border.default' : 'border.subtle'}
        aria-pressed={isActive}
        aria-expanded={isActive}
        aria-label={category.pillLabel}
      >
        <Icon size={14} aria-hidden />
        {category.pillLabel}
      </Button>
    );
  };

  if (pillRowIndexes && pillRowIndexes.length > 0) {
    return (
      <Box w="full">
        {pillRowIndexes.map((rowIndexes, rowIndex) => (
          <Flex
            key={rowIndex}
            gap={2}
            w="full"
            px={1}
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            mt={rowIndex > 0 ? 2 : 0}
          >
            {rowIndexes.map((categoryIndex) => {
              const category = categories[categoryIndex];

              if (!category) {
                return null;
              }

              return renderPill(category.id);
            })}
          </Flex>
        ))}
      </Box>
    );
  }

  return (
    <Flex
      gap={2}
      w="full"
      justifyContent="center"
      alignItems="center"
      flexWrap="wrap"
      py={variant === 'text-pill' ? 4 : 0}
    >
      {categories.map((category) => renderPill(category.id))}
    </Flex>
  );
}
